/**
 * Server Proxy Media Provider
 *
 * Routes stock photo/video searches through the api-server proxy endpoints
 * instead of calling external APIs directly from the browser.
 * This avoids needing VITE_PEXELS_API_KEY etc. in the frontend build.
 */

import api from './api';
import { MediaProvider, MediaSearchResponse, MediaPhoto, MediaVideo } from './mediaProvider';

interface ProxyPhoto {
  id: number | string;
  url: string;
  thumbnailUrl: string;
  photographer: string;
  source: string;
  width: number;
  height: number;
  alt: string;
}

interface ProxyVideo {
  id: number | string;
  url: string;
  thumbnailUrl: string;
  duration: number;
  width: number;
  height: number;
  source: string;
}

function toMediaPhoto(p: ProxyPhoto): MediaPhoto {
  return {
    id: p.id,
    width: p.width,
    height: p.height,
    url: p.url,
    photographer: p.photographer || 'Unknown',
    photographer_url: '',
    avg_color: null,
    src: {
      original: p.url,
      large2x: p.url,
      large: p.url,
      medium: p.thumbnailUrl || p.url,
      small: p.thumbnailUrl || p.url,
      portrait: p.url,
      landscape: p.url,
      tiny: p.thumbnailUrl || p.url,
    },
    alt: p.alt || '',
    source: 'pexels',
  };
}

function toMediaVideo(v: ProxyVideo): MediaVideo {
  return {
    id: v.id,
    width: v.width,
    height: v.height,
    duration: v.duration,
    image: v.thumbnailUrl || '',
    url: v.url,
    user: { name: 'Pexels', url: '' },
    video_files: [
      {
        id: v.id,
        quality: 'hd',
        file_type: 'video/mp4',
        width: v.width,
        height: v.height,
        link: v.url,
      },
    ],
    source: 'pexels',
  };
}

class ServerProxyProvider implements MediaProvider {
  name = 'server-proxy';

  async searchPhotos(query: string, page = 1, perPage = 20): Promise<MediaSearchResponse<MediaPhoto>> {
    const response = await api.post('/media/stock-photos', {
      query,
      limit: perPage,
    });
    const data = response.data;
    const photos = (data.photos || []).map(toMediaPhoto);
    return {
      page,
      per_page: perPage,
      total_results: data.total || photos.length,
      photos,
      next_page: photos.length >= perPage ? `page=${page + 1}` : undefined,
    };
  }

  async searchVideos(query: string, page = 1, perPage = 10): Promise<MediaSearchResponse<MediaVideo>> {
    const response = await api.post('/media/stock-videos', {
      query,
      limit: perPage,
    });
    const data = response.data;
    const videos = (data.videos || []).map(toMediaVideo);
    return {
      page,
      per_page: perPage,
      total_results: data.total || videos.length,
      videos,
      next_page: videos.length >= perPage ? `page=${page + 1}` : undefined,
    };
  }

  async getCuratedPhotos(page = 1, perPage = 20): Promise<MediaSearchResponse<MediaPhoto>> {
    // Use a generic curated query since the proxy doesn't have a dedicated curated endpoint
    return this.searchPhotos('nature landscape', page, perPage);
  }

  async getPopularVideos(page = 1, perPage = 10): Promise<MediaSearchResponse<MediaVideo>> {
    return this.searchVideos('cinematic aerial', page, perPage);
  }
}

export const serverProxyAPI: MediaProvider = new ServerProxyProvider();
